import { TextField, Box, useTheme } from '@mui/material';
import { requiredOutlineSx } from '../utils/requiredFieldSx';
import { useUiPrefs } from '../contexts/UiPrefsContext';

// Dense sizing for desktop table cells (the prop-free call sites). Those
// rows only render when the table itself renders, and card view replaces
// the table at xs, so no breakpoint values are needed here. Exported so
// tests can pin the values.
export const numberFieldSx = {
  flex: '0 1 auto',
  minWidth: 0,
  width: '70px',
  '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' },
  '& .MuiInputBase-input': { fontSize: '0.75rem', padding: '4px 6px' },
  '& .MuiInputBase-input::placeholder': { fontSize: '0.75rem' },
};

// #1576 follow-up: the entry surfaces (quick-add Sheet + card-view edit) are
// NOT xs-only - card view also triggers on desktop table overflow, and
// landscape phones sit past the sm breakpoint - so viewport breakpoints
// cannot scope them. Call sites mark themselves with the fullRow prop
// instead. Sizing per convention 12 (#1425): 16px font (the iOS focus-zoom
// threshold) at every width, fields sized for "$XX,XXX.XX" at that font
// (the ">= 20% over 56px" rule is a floor, not a ceiling).
export const entryFieldSx = {
  flex: '0 1 auto',
  minWidth: 0,
  width: '112px',
  '& .MuiInputBase-root': { height: '40px', fontSize: '1rem' },
  '& .MuiInputBase-input': { fontSize: '1rem', padding: '8px 8px' },
  '& .MuiInputBase-input::placeholder': { fontSize: '1rem' },
};

// fullRow pair wrapper: the debit/credit pair spans the row - debit hugs
// the left edge, credit the right. Dense table cells keep the plain
// content-width wrapper. singleAmount keeps a plain wrapper too (one
// field, nothing to split).
export const entryRowSx = {
  display: 'flex',
  gap: 0.5,
  width: '100%',
  justifyContent: 'space-between',
};

// The placeholder words ("debit"/"credit"/"amount") carry a faded in-out hue as a
// quiet reinforcement of direction (#1281): debit = money out (negative/red),
// credit = money in (positive/green). Low opacity keeps it as understated as the
// default grey, just tinted - no box fill or border change. Merges into the base
// placeholder rule so its font-size is preserved (same key would otherwise win).
export const placeholderHueSx = (color, base = numberFieldSx) => ({
  ...base,
  '& .MuiInputBase-input::placeholder': {
    ...base['& .MuiInputBase-input::placeholder'],
    color,
    opacity: 0.6,
  },
});

// Currency fields open the numeric keypad, never the full keyboard, via
// inputMode="decimal" (keeps the decimal point). type stays "text" so
// formatting and accounting parens survive and no stepper spinner exists
// (convention 12); the save-path parse is already string-based.
const amountInputProps = { inputMode: 'decimal' };

const AmountInputCell = ({
  debitValue,
  creditValue,
  onDebitChange,
  onCreditChange,
  debitRef,
  creditRef,
  // Future Expenses (budget type 2): a single positive "add" amount, no
  // debit/credit pair (#1209). The value maps to the credit field so the
  // existing `credit - debit` save math yields a positive amountCents.
  singleAmount = false,
  singlePlaceholder = 'amount',
  // Entry surfaces (Sheet / card edit) opt in to the wide split layout;
  // dense desktop table cells stay prop-free (#1576).
  fullRow = false,
  // A paycheck (#2288) is a deposit: the debit field is disabled, its title
  // says why.
  depositOnly = false,
  depositOnlyTitle = 'Deposits only',
}) => {
  const theme = useTheme();
  // 'UI Tips' OFF (#1282) = plain fields: no in/out hue, no required outline. Both
  // cues are gated by this one flag; the MUI focus ring is untouched so keyboard
  // focus stays visible.
  const { uiTips } = useUiPrefs();
  // Empty = required prompt (indigo accent, #1210), not an error.
  const reqSx = requiredOutlineSx(theme);
  const baseSx = fullRow ? entryFieldSx : numberFieldSx;
  // Per-field placeholder hue (#1281): debit = money out (negative/red), credit
  // and the single Future-Expenses field = money in (positive/green). Same value-
  // colour tokens the app uses for amounts (resolveAmountColor in AmountDisplay:
  // negative -> error.main, positive -> success.main). Border outline (#1210) and
  // placeholder colour target different elements, so they layer cleanly when empty.
  const debitSx = uiTips ? placeholderHueSx(theme.palette.error.main, baseSx) : baseSx;
  const creditSx = uiTips ? placeholderHueSx(theme.palette.success.main, baseSx) : baseSx;
  const handleDebitChange = (e) => {
    const val = e.target.value;
    onDebitChange(val);
    if (val) onCreditChange('');
  };

  const handleCreditChange = (e) => {
    const val = e.target.value;
    onCreditChange(val);
    if (!singleAmount && val) onDebitChange('');
  };

  if (singleAmount) {
    const isEmpty = !creditValue || creditValue === '';
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <TextField
          inputRef={creditRef}
          type="text"
          value={creditValue || ''}
          onChange={handleCreditChange}
          size="small"
          inputProps={amountInputProps}
          sx={isEmpty && uiTips ? { ...creditSx, ...reqSx } : creditSx}
          placeholder={singlePlaceholder}
        />
      </Box>
    );
  }

  // Both empty = the line still needs an amount: prompt with the required
  // outline (indigo, #1210), not the error treatment.
  const isEmpty = (!debitValue || debitValue === '') && (!creditValue || creditValue === '');
  const debitFieldSx = isEmpty && uiTips ? { ...debitSx, ...reqSx } : debitSx;
  const creditFieldSx = isEmpty && uiTips ? { ...creditSx, ...reqSx } : creditSx;

  return (
    <Box sx={fullRow ? entryRowSx : { display: 'flex', gap: 0.5 }}>
      <TextField
        inputRef={debitRef}
        type="text"
        value={debitValue || ''}
        onChange={handleDebitChange}
        size="small"
        inputProps={amountInputProps}
        sx={debitFieldSx}
        placeholder="debit"
        disabled={depositOnly}
        title={depositOnly ? depositOnlyTitle : undefined}
      />
      <TextField
        inputRef={creditRef}
        type="text"
        value={creditValue || ''}
        onChange={handleCreditChange}
        size="small"
        inputProps={amountInputProps}
        sx={creditFieldSx}
        placeholder="credit"
      />
    </Box>
  );
};

export default AmountInputCell;
